// client/src/components/billingExecution/BillingPreview.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as ApprovedIcon,
  Cancel as NotApprovedIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingReceiptService from '../../services/billingReceiptService';

const BillingPreview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Current language
  const lang = i18n.language;
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Download options dialog
  const [downloadOptionsOpen, setDownloadOptionsOpen] = useState(false);
  
  // Check if user can review (is checker or admin)
  const canReview = user.role === 'admin' || 
                    (user.role === 'faculty' && 
                    (user.user_type === 'checker' || user.user_type === 'both'));
  
  // Check if user can download (is creator, checker, admin, or receipt is approved)
  const canDownload = (receipt) => {
    if (!receipt) return false;
    
    if (receipt.is_approved) return true;
    if (user.role === 'admin') return true;
    if (receipt.created_by === user.id) return true;
    if (user.role === 'faculty' && 
        (user.user_type === 'checker' || user.user_type === 'both')) {
      return true;
    }
    
    return false;
  };
  
  // Load receipt data
  useEffect(() => {
    const loadReceipt = async () => {
      try {
        setLoading(true);
        const response = await billingReceiptService.getBillingReceipt(id);
        setReceipt(response.data);
        setError('');
      } catch (err) {
        setError(t('errorLoadingReceiptDetails'));
      } finally {
        setLoading(false);
      }
    };
    
    loadReceipt();
  }, [id, t]);
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notSpecified');
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (!value) return formatCurrency(0);
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(parseFloat(value));
  };
  
  // Calculate totals
  const calculateTotals = () => {
    if (!receipt || !receipt.items || !receipt.transaction) {
      return { baseTotal: 0, additionalTotal: 0, grandTotal: 0 };
    }
    
    const baseTotal = receipt.items.length * parseFloat(receipt.transaction.total_value || 0);
    const additionalTotal = receipt.items.reduce((sum, item) => sum + parseFloat(item.additional_value || 0), 0);
    
    return {
      baseTotal,
      additionalTotal,
      grandTotal: baseTotal + additionalTotal
    };
  };
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Open download options dialog
  const handleDownloadClick = () => {
    if (!canDownload(receipt)) {
      setError(t('cannotDownloadUnapprovedReceipt'));
      return;
    }
    
    setDownloadOptionsOpen(true);
  };
  
  // Close download options dialog
  const handleCloseDownloadOptions = () => {
    setDownloadOptionsOpen(false);
  };
  
  // Handle download excel (detailed)
  const handleDownloadDetailed = async () => {
    try {
      await billingReceiptService.downloadReceiptExcel(id, false);
      setDownloadOptionsOpen(false);
    } catch (err) {
      setError(t('errorDownloadingExcel'));
    }
  };
  
  // Handle download excel (summarized)
  const handleDownloadSummarized = async () => {
    try {
      await billingReceiptService.downloadReceiptExcel(id, true);
      setDownloadOptionsOpen(false);
    } catch (err) {
      setError(t('errorDownloadingExcel'));
    }
  };
  
  // Handle download distribution (detailed)
  const handleDownloadDistributionDetailed = async () => {
    try {
      await billingReceiptService.downloadReceiptDistributionExcel(id, false);
      setDownloadOptionsOpen(false);
    } catch (err) {
      setError(t('errorDownloadingDistributionExcel'));
    }
  };
  
  // Handle download distribution (summarized)
  const handleDownloadDistributionSummarized = async () => {
    try {
      await billingReceiptService.downloadReceiptDistributionExcel(id, true);
      setDownloadOptionsOpen(false);
    } catch (err) {
      setError(t('errorDownloadingDistributionExcel'));
    }
  };
  
  // Print function
  const handlePrint = () => {
    window.print();
  };
  
  const totals = calculateTotals();
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  if (!receipt) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">{t('receiptNotFound')}</Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4 }} className="billing-preview-container">
      <Typography variant="h4" gutterBottom>
        {t('billingReceiptDetails')}
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} className="no-print">
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }} className="no-print-shadow">
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            {t('receiptID')}: {receipt.id}
          </Typography>
          
          {receipt.is_approved ? (
            <Tooltip title={`${t('approvedBy')}: ${receipt.approver?.username || t('unknown')}`}>
              <Chip 
                icon={<ApprovedIcon />}
                label={t('approved')} 
                color="success" 
                size="medium"
              />
            </Tooltip>
          ) : (
            <Tooltip title={t('notApprovedYet')}>
              <Chip 
                icon={<NotApprovedIcon />}
                label={t('notApproved')} 
                color="warning" 
                size="medium"
              />
            </Tooltip>
          )}
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('userFaculty')}:</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {receipt.userFaculty ? receipt.userFaculty[`name_${lang}`] : t('none')}
            </Typography>
            
            <Typography variant="subtitle1">{t('paymentBylaw')}:</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {receipt.paymentBylaw ? receipt.paymentBylaw.name : '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('transactionName')}:</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {receipt.transaction?.paymentItem?.name || '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('createdBy')}:</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {receipt.creator?.username || '-'}
              {receipt.created_at && ` (${formatDate(receipt.created_at)})`}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('billingDueDate')}:</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>{formatDate(receipt.billing_due_date)}</Typography>
            
            <Typography variant="subtitle1">{t('status')}:</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <Chip 
                label={t(receipt.status)}
                color={getStatusColor(receipt.status)}
                size="small"
              />
            </Typography>
            
            <Typography variant="subtitle1">{t('baseValuePerStudent')}:</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>{formatCurrency(receipt.transaction.total_value)}</Typography>
            
            <Typography variant="subtitle1">{t('totalStudents')}:</Typography>
            <Typography variant="body1">{receipt.items?.length || 0}</Typography>
          </Grid>
        </Grid>
        
        {receipt.is_approved && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('approvalDetails')}:
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t('approvedBy')}: {receipt.approver?.username || '-'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t('approvedAt')}: {formatDate(receipt.approved_at)}
            </Typography>
            {receipt.comments && (
              <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
                {t('comments')}: {receipt.comments}
              </Typography>
            )}
          </Box>
        )}
        
        {!receipt.is_approved && receipt.comments && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('reviewComments')}:
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {receipt.comments}
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ mb: 2 }}>
          <Grid container>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">{t('baseTotal')}:</Typography>
              <Typography variant="body1" fontWeight="bold">{formatCurrency(totals.baseTotal)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">{t('additionalTotal')}:</Typography>
              <Typography variant="body1" fontWeight="bold">{formatCurrency(totals.additionalTotal)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">{t('grandTotal')}:</Typography>
              <Typography variant="h6" color="primary">{formatCurrency(totals.grandTotal)}</Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }} className="no-print">
          {/* Review button - only for checker or admin and only for unapproved receipts */}
          {canReview && !receipt.is_approved && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<ReviewIcon />}
              onClick={() => navigate(`/billing-receipts/review/${receipt.id}`)}
            >
              {t('reviewReceipt')}
            </Button>
          )}
          
          {/* Download button - disabled if not allowed */}
          <Button
            variant="contained"
            color="info"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadClick}
            disabled={!canDownload(receipt)}
          >
            {t('download')}
          </Button>
          
          {/* Print button */}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            {t('print')}
          </Button>
        </Box>
      </Paper>
      
      <Typography variant="h6" gutterBottom>
        {t('billingItems')}
      </Typography>
      
      {receipt.items && receipt.items.length > 0 ? (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="billing items table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('nationalID')}</TableCell>
                  <TableCell>{t('nameEN')}</TableCell>
                  <TableCell>{t('nameAR')}</TableCell>
                  <TableCell align="right">{t('baseValue')}</TableCell>
                  <TableCell align="right">{t('additionalValue')}</TableCell>
                  <TableCell align="right">{t('totalValue')}</TableCell>
                  <TableCell align="center">{t('status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipt.items
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.national_id}</TableCell>
                      <TableCell>{item.name_en}</TableCell>
                      <TableCell>{item.name_ar}</TableCell>
                      <TableCell align="right">{formatCurrency(receipt.transaction.total_value)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.additional_value)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(parseFloat(receipt.transaction.total_value || 0) + parseFloat(item.additional_value || 0))}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={t(item.status)}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={receipt.items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('rowsPerPage')}
          />
        </Paper>
      ) : (
        <Alert severity="info">
          {t('noItemsFound')}
        </Alert>
      )}
      
      <Box sx={{ mt: 3 }} className="no-print">
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/billing-receipts')}
        >
          {t('backToReceipts')}
        </Button>
      </Box>
      
      {/* Download Options Dialog */}
      <Dialog
        open={downloadOptionsOpen}
        onClose={handleCloseDownloadOptions}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('downloadOptions')}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {t('receiptID')}: {receipt.id}
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            {t('studentList')}:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleDownloadDetailed}
            >
              {t('detailedView')}
            </Button>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={handleDownloadSummarized}
            >
              {t('summarizedView')}
            </Button>
          </Box>
          
          <Typography variant="subtitle2" gutterBottom>
            {t('accountDistribution')}:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="secondary"
              onClick={handleDownloadDistributionDetailed}
            >
              {t('detailedDistribution')}
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={handleDownloadDistributionSummarized}
            >
              {t('summarizedDistribution')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDownloadOptions}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>
      
      {/* Print-specific styles */}
      <style jsx="true">{`
        @media print {
          .no-print {
            display: none !important;
          }
          .no-print-shadow {
            box-shadow: none !important;
          }
          .billing-preview-container {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default BillingPreview;